import { Component, Inject, OnDestroy, OnInit, ViewChild, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { FormControl } from '@angular/forms';
import { ApiService } from '../../api/api.service';
import { EnvironmentService } from '../../environment/environment.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Apollo } from 'apollo-angular';

import { nav_search_all } from './graphql/navSearchAll';
import { nav_search_ein } from './graphql/navSearchEIN';
import { nav_search_projects } from './graphql/navSearchProjects';
import { nav_search_funds } from './graphql/navSearchFunds';
import { nav_search_organizations } from './graphql/navSearchOrganizations';
import { nav_search_sponsors } from './graphql/navSearchSponsors';
import { nav_search_people } from './graphql/navSearchPeople';


//import GraphQL Query files
const NAV_SEARCH_ALL = nav_search_all;
const NAV_SEARCH_EIN = nav_search_ein;
const NAV_SEARCH_PROJECTS = nav_search_projects;
const NAV_SEARCH_FUNDS = nav_search_funds;
const NAV_SEARCH_ORGANIZATIONS = nav_search_organizations;
const NAV_SEARCH_SPONSORS = nav_search_sponsors;
const NAV_SEARCH_PEOPLE = nav_search_people;

@Component({
    selector: 'app-cw-search',
    templateUrl: './cw-search.component.html',
    styleUrls: ['./cw-search.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CwSearchComponent implements OnInit, OnDestroy {
  private querySubscription: Subscription = new Subscription();
  searchText: FormControl = new FormControl('');
  storeSearchText: string = '';
  searchSelect: FormControl = new FormControl('All');
  searchType: Array<string> = [
    'All',
    'Deals',
    'Dead Deals',
    'Funds',
    'EIN',
    'Sponsors',
    'Organizations',
    'People',
  ];
  typeSubscription: Subscription = new Subscription();
  selectSubscription: Subscription = new Subscription();
  searchResults: Array<any> = [];
  loading: Boolean = false;
  currentPage: number = 0;

  @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger })
  autoTrigger?: MatAutocompleteTrigger;

  constructor(
    private apiService: ApiService,
    private env: EnvironmentService,
    private apollo: Apollo,
    @Inject(DOCUMENT) public document: Document
  ) {
    //If search text changes
    this.typeSubscription = this.searchText.valueChanges
      .pipe(
        debounceTime(500), // Waiting for 500ms while you're typing
        distinctUntilChanged() // Prevents the emitting if the 'start' value and the 'end' value are the same
      )
      .subscribe((value) => {
        this.storeSearchText = value;
        this.searchSubscription(value);
      });

    //If search type changes
    this.selectSubscription = this.searchSelect.valueChanges
      .pipe(
        distinctUntilChanged() // Prevents the emitting if the 'start' value and the 'end' value are the same
      )
      .subscribe((value) => {
        if (this.searchText.value !== '') {
          this.searchResults = [];
          this.currentPage = 0;
          this.searchSubscription(value);
        }
      });
  }

  ngOnInit(): void {}

  //Makes API call to GraphQL for search results
  searchSubscription(value: any, event?: any, clickmore?: boolean): void {
    //Keeps result panel open when "load more" is clicked in the results
    if (event) {
      event.stopPropagation();
      if (this.autoTrigger) {
        this.autoTrigger.openPanel();
      }
    }

    if (!clickmore) {
      this.searchResults = [];
      this.currentPage = 0;
    }

    if (value && value.trim().length > 0) {
      this.loading = true;
      if (this.searchSelect.value === 'All') {
        this.querySubscription = this.apollo
          .watchQuery<any>({
            query: NAV_SEARCH_ALL,
            variables: {
              searchLike: '%' + this.storeSearchText.trim() + '%',
              search: this.storeSearchText.trim(),
              limit: 10,
            },
          })
          .valueChanges.subscribe(({ data }) => {
            if(data){
              this.loading = false;
              this.formatResults(data);
            }
          });
      } else if (this.searchSelect.value === 'EIN') {
        this.querySubscription = this.apollo
          .watchQuery<any>({
            query: NAV_SEARCH_EIN,
            variables: {
              ein: this.storeSearchText.trim(),
            },
          })
          .valueChanges.subscribe(({ data }) => {
            if(data){
              this.loading = false;
              this.formatResults(data);
            }
          });
      } else {
        let selectQuery: any;
        if (this.searchSelect.value === 'Deals') {
          selectQuery = NAV_SEARCH_PROJECTS;
        } else if(this.searchSelect.value === 'Dead Deals') {
          selectQuery = NAV_SEARCH_PROJECTS;
        } else if(this.searchSelect.value === 'Funds') {
          selectQuery = NAV_SEARCH_FUNDS;
        } else if (this.searchSelect.value === 'Organizations') {
          selectQuery = NAV_SEARCH_ORGANIZATIONS;
        } else if (this.searchSelect.value === 'Sponsors') {
          selectQuery = NAV_SEARCH_SPONSORS;
        } else if (this.searchSelect.value === 'People') {
          selectQuery = NAV_SEARCH_PEOPLE;
        }
        this.querySubscription = this.apollo
          .watchQuery<any>({
            query: selectQuery,
            variables: {
              searchLike: '%' + this.storeSearchText.trim() + '%',
              search: this.storeSearchText.trim(),
              limit: 10,
              page: this.currentPage + 1,
            },
          })
          .valueChanges.subscribe(({ data }) => {
            if(data){
              this.loading = false;
              this.formatResults(data);
            }
          });
      }

      this.searchText.setValue(this.storeSearchText); //Reset form object after being cleared out by the "load more" button click
    }
  }

  //Formats the GraphQL Query Results
  formatResults(results: any) {
    let arrayConvert: Array<any> = [];

    if (results) {
      if (
        this.searchSelect.value === 'EIN' &&
        (results.get_project_by_ein.length > 0 ||
          results.get_fund_by_ein.length > 0)
      ) {
        let temp: any = {
          keyname: 'EIN Match',
          data:
            results.get_project_by_ein.length > 0
              ? results.get_project_by_ein
              : results.get_fund_by_ein,
        };
        arrayConvert.push(temp);
      } else if (this.searchSelect.value === 'All') {
        Object.keys(results).forEach((key: any) => {
          if (results[key].data.length > 0) {
            //Remove 'search_' from string and capitalize first letter of keyname
            let keyname: string = key.slice(7);

            // If keyname includes '_',
            if(keyname.includes('_')){
              keyname = keyname.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
            }

            let strippedkeyname =
              keyname.charAt(0).toUpperCase() + keyname.slice(1);

            let dataResult = results[key].data
            if(key === 'search_projects'){
              dataResult = this.filterDeadProjects(results[key].data);
            }
            if(key === 'search_owned_entities'){
              dataResult = results[key].data.filter((val:any) => val.project!==null)
            }
            //Convert obj to array for loop iterables
            // arrayConvert.push(results[key]);
            arrayConvert.push({
              data: dataResult,//results[key].data,
              keyname: strippedkeyname,
            });
          }
        });
      } else {
        Object.keys(results).forEach((key: any) => {
          if (results[key].data.length > 0) {
            if (this.searchResults.length > 0) {
              results[key]['data'].forEach((val: object) => {
                this.searchResults[0]['data'] = Object.assign(
                  [],
                  this.searchResults[0]['data']
                );
                this.searchResults[0]['data'].push(val);
              });
              this.searchResults[0]['paginatorInfo'] =
                results[key]['paginatorInfo'];
            } else {
              //Remove 'search_' from string and capitalize first letter of keyname
              let keyname: string = key.slice(7);
              let strippedkeyname =
                keyname.charAt(0).toUpperCase() + keyname.slice(1);

              let dataResult = results[key].data

              if(key === 'search_projects'){
                if(this.searchSelect.value === 'Dead Deals'){
                  dataResult = this.filterDeadProjects(results[key].data, true)
                } else {
                  dataResult = this.filterDeadProjects(results[key].data)
                }
              }
              //Convert obj to array for loop iterables
              // arrayConvert.push(results[key]);
              arrayConvert.push({
                data: dataResult,///results[key].data,
                keyname: strippedkeyname,
                paginatorInfo: results[key].paginatorInfo,
              });
            }
            this.currentPage = results[key]['paginatorInfo']['currentPage'];
          }
        });
      }
    }

    if (arrayConvert.length > 0) {
      this.searchResults = arrayConvert;
    }
  }

  //Reroutes to the appropriate page on CREAWeb
  searchRedirect(route: string, id: string): void {
    let url: string = this.env.nova_url;
    if (route === 'Projects' || route === 'Project') {
      url = this.env.nova_url
      url = '/deal/' + id;
    } else if (route === 'Funds' || route === 'Fund') {
      url = this.env.nova_url
      url += '/fund/' + id;
    } else if (route === 'Sponsors') {
      url += '/contacts/sponsors/' + id;
    } else if (route === 'Organizations') {
      url += '/contacts/organizations/' + id;
    } else if (route === 'People') {
      url += '/contacts/people/' + id;
    }
    this.document.location.assign(url);
  }

  //Will change search type and trigger the subscription to make API calls
  changeSearchType(type: string): void {
    this.searchSelect.setValue(type);
  }

  //Filters out projects with 'dead' status by default; reverse shows only 'dead' status projects
  filterDeadProjects(data: any, reverse?: boolean){
    let activeProjects : Array<any> = []
    let deadProjects : Array<any> = []
    data.forEach((result:any) => {
      let project:any  = result;
      let isDeadSequence:boolean = false;
      for(let i = 0; i < project.projectStatuses.length; i++){
        if(project.projectStatuses[i].active==true && project.projectStatuses[i].status_sequence_id=="2"){
          isDeadSequence = true;
        }
      }
      if(!isDeadSequence){
        activeProjects.push(result)
      } else {
        deadProjects.push(result)
      }
    })
    return reverse ? deadProjects : activeProjects;
  }

  //Unsubscribe to observables after re-route
  ngOnDestroy(): void {
    this.typeSubscription.unsubscribe();
    this.selectSubscription.unsubscribe();
    this.querySubscription.unsubscribe();
  }
}
